import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Button } from '../../styles/common/Button.styled';
import PageHeader from '../common/PageHeader';
import PageState from '../common/PageState';
import ConfirmModal from '../common/ConfirmModal';
import Pagination from '../common/Pagination';
import { formatDate, formatDuration } from '../../utils/format';
import toast from 'react-hot-toast';
import {
  getAudioFileList,
  deleteAudioFile,
  downloadAudioFile,
  AudioFileResponse
} from '../../api/audio';
import AudioUpdateModal from './AudioUpdateModal';
import AudioUploadModal from './AudioUploadModal';
import AudioPlayerModal from './AudioPlayerModal';
import * as T from '../../styles/common/table.styled';
import DebouncedSearchInput from '../common/DebouncedSearchInput';

/* ── 업로드 드래그 영역 ── */
const UploadArea = styled.div<{ $isDragActive: boolean }>`display: grid; grid-template-columns: 32px minmax(0,1fr) auto; align-items: center; gap: 4px 16px; padding: 16px; border: 1px dashed ${({ theme, $isDragActive }) => $isDragActive ? theme.color.point_y_100 : theme.color.primary_400}; border-radius: 8px; background: ${({ theme, $isDragActive }) => $isDragActive ? theme.color.primary_50 : theme.color.primary_100}; > svg { width: 32px; height: 32px; color: ${({ theme }) => theme.color.text_color_300} !important; }`;

const UploadIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
    <path d="M12 14V4M12 4L8 8M12 4L16 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M20 12V17C20 19.2091 18.2091 21 16 21H8C5.79086 21 4 19.2091 4 17V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const UploadText = styled.div`font-size: 12px; color: ${({ theme }) => theme.color.text_color_300}; strong { display: block; color: ${({ theme }) => theme.color.text_color_100}; font-size: 13px; font-weight: 500; margin-bottom: 4px; }`;

const CopyrightNotice = styled.p`grid-column: 2 / -1; font-size: 11px; color: ${({ theme }) => theme.color.text_color_300}; margin: 2px 0 0;`;

/* ── 재생/일시정지 버튼 ── */
const PlayButton = styled(T.ActionButton)<{ $isPlaying: boolean }>`width: 28px; padding: 0; svg { width: 14px; height: 14px; }`;

const PlayIcon  = () => <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>;


/* ── 유틸 ── */
const formatSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const AudioList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [isDragActive, setIsDragActive] = useState(false);
  const [playingId, setPlayingId] = useState<number | null>(null);
  const [audioList, setAudioList] = useState<AudioFileResponse[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const deletePending = useRef(false);
  const [deletingAudio, setDeletingAudio] = useState<AudioFileResponse | null>(null);
  const [searchName, setSearchName] = useState('');
  const [appliedName, setAppliedName] = useState('');
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedAudio, setSelectedAudio] = useState<AudioFileResponse | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);
  const [playbackInfo, setPlaybackInfo] = useState<{ url: string; name: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchAudioList = useCallback(async (page: number, name?: string) => {
    setIsLoading(true);
    setHasError(false);
    try {
      const data = await getAudioFileList({ pg: page, limit: 10, name });
      setAudioList(data.content);
      setTotalElements(data.totalElements);
      setTotalPages(data.totalPages);
    } catch {
      setHasError(true);
      toast.error('음원 목록을 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchAudioList(currentPage, appliedName); }, [currentPage, appliedName, fetchAudioList]);

  const handleSearch = useCallback((nextName?: string) => {
    setAppliedName(typeof nextName === 'string' ? nextName : searchName);
    setCurrentPage(1);
  }, [searchName]);

  const handleFileUpload = (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!ext || !['wav', 'mp3'].includes(ext)) { toast.error('wav, mp3 파일만 업로드 가능합니다.'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('파일 용량은 10MB를 초과할 수 없습니다.'); return; }
    const audio = new Audio();
    audio.src = URL.createObjectURL(file);
    audio.onloadedmetadata = () => {
      URL.revokeObjectURL(audio.src);
      if (audio.duration > 240) { toast.error('음원 재생 시간은 4분을 초과할 수 없습니다.'); return; }
      setUploadFile(file);
      setIsUploadModalOpen(true);
    };
    audio.onerror = () => { URL.revokeObjectURL(audio.src); toast.error('올바른 오디오 파일이 아닙니다.'); };
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) handleFileUpload(files[0]);
  };

  const handleDelete = async (id: number) => {
    if (deletePending.current) return;
    deletePending.current = true; setIsDeleting(true);
    try {
      await deleteAudioFile(id);
      setDeletingAudio(null);
      toast.success('삭제되었습니다.');
      fetchAudioList(currentPage, appliedName);
    } catch {
      toast.error('삭제에 실패했습니다.');
    } finally { deletePending.current = false; setIsDeleting(false); }
  };

  const togglePlay = async (item: AudioFileResponse) => {
    if (playbackInfo) { URL.revokeObjectURL(playbackInfo.url); setPlaybackInfo(null); }
    try {
      setPlayingId(item.id);
      const blob = await downloadAudioFile(item.id);
      const url = URL.createObjectURL(blob);
      setPlaybackInfo({ url, name: item.name });
      setIsPlayerModalOpen(true);
    } catch {
      toast.error('음원을 재생할 수 없습니다.');
      setPlayingId(null);
    }
  };

  const handleClosePlayer = () => {
    if (playbackInfo) URL.revokeObjectURL(playbackInfo.url);
    setIsPlayerModalOpen(false);
    setPlaybackInfo(null);
    setPlayingId(null);
  };

  return (
    <T.PageContainer>
      <PageHeader title="음원 파일 관리" count={isLoading || hasError ? undefined : totalElements} />
      {/* 업로드 드래그 영역 */}
      <UploadArea
        $isDragActive={isDragActive}
        onDragOver={(e) => { e.preventDefault(); setIsDragActive(true); }}
        onDragLeave={(e) => { e.preventDefault(); setIsDragActive(false); }}
        onDrop={handleDrop}
      >
        <input type="file" ref={fileInputRef} style={{ display: 'none' }}
          accept=".wav,.mp3,audio/wav,audio/mpeg"
          onChange={(e) => { const f = e.target.files; if (f?.[0]) handleFileUpload(f[0]); }}
        />
        <UploadIcon />
        <UploadText>
          <strong>음원을 드래그하거나 파일을 선택하세요.</strong>
          <span>wav, mp3 지원 (최대 10MB, 4분 이내)</span>
        </UploadText>
        <Button $variant="secondary" onClick={() => fileInputRef.current?.click()}>파일 선택</Button>
        <CopyrightNotice>저작권에 위반되는 음원 파일 사용에 주의해야 합니다.</CopyrightNotice>
      </UploadArea>

      {/* 검색 바 */}
      <T.TopBar>
        <T.SearchGroup>
          <T.SearchInput as={DebouncedSearchInput}
            aria-label="음원명 검색" placeholder="음원명 검색"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            onSearch={handleSearch}
            delay={250}
            style={{ width: '240px' }}
          />
          <T.SearchButton onClick={() => handleSearch()}>검색</T.SearchButton>
        </T.SearchGroup>
      </T.TopBar>



      <T.TableArea $stickyActions $minWidth={980}>
        <T.Table>
          <colgroup>
            <col style={{ width: '60px' }} />
            <col style={{ width: '180px' }} />
            <col />
            <col style={{ width: '80px' }} />
            <col style={{ width: '120px' }} />
            <col style={{ width: '170px' }} />
            <col style={{ width: '150px' }} />
          </colgroup>
          <thead>
            <tr>
              <T.Th>No.</T.Th>
              <T.Th>음원명</T.Th>
              <T.Th>파일명</T.Th>
              <T.Th>길이</T.Th>
              <T.Th>용량</T.Th>
              <T.Th>생성일</T.Th>
              <T.Th style={{ textAlign: 'center' }}>관리</T.Th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <T.Tr><T.Td colSpan={7} style={{ textAlign: 'center' }}>로딩 중...</T.Td></T.Tr>
            ) : hasError ? <T.Tr><T.Td colSpan={7}><PageState status="error" onRetry={() => fetchAudioList(currentPage, appliedName)} /></T.Td></T.Tr> : audioList.length === 0 ? (
              <T.Tr><T.Td colSpan={7}><T.NoData>{appliedName ? '검색 조건에 맞는 음원이 없습니다.' : '등록된 음원이 없습니다.'}</T.NoData></T.Td></T.Tr>
            ) : (
              audioList.map((item, index) => (
                <T.Tr key={item.id}>
                  <T.Td>{totalElements - (currentPage - 1) * 10 - index}</T.Td>
                  <T.Td>
                    <T.ActionButton title={item.name}
                      style={{ maxWidth: '100%', justifyContent: 'flex-start', padding: 0 }}
                      onClick={() => { setSelectedAudio(item); setIsUpdateModalOpen(true); }}
                    >
                      {item.name}
                    </T.ActionButton>
                  </T.Td>
                  <T.Td title={item.originalFilename}>{item.originalFilename}</T.Td>
                  <T.Td>{formatDuration(item.audioDuration)}</T.Td>
                  <T.Td>{formatSize(item.filesize)}</T.Td>
                  <T.Td>{formatDate(item.createdAt)}</T.Td>
                  <T.Td style={{ textAlign: 'center' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '6px' }}>
                      <PlayButton $isPlaying={playingId === item.id} onClick={() => togglePlay(item)} title="재생" aria-label={`${item.name} 재생`}>
                        <PlayIcon />
                      </PlayButton>
                      <T.ActionButton $variant="danger" onClick={() => setDeletingAudio(item)}>삭제</T.ActionButton>
                    </div>
                  </T.Td>
                </T.Tr>
              ))
            )}
          </tbody>
        </T.Table>
      </T.TableArea>

      <T.TableFooter>{!isLoading && !hasError && <T.TotalCount>총 <span>{totalElements}</span>건</T.TotalCount>}<Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} /></T.TableFooter>

      {deletingAudio && <ConfirmModal pending={isDeleting} title="음원 삭제" message={`'${deletingAudio.name}' 음원을 삭제하시겠습니까?\n삭제된 파일은 복구할 수 없습니다.`} confirmLabel="삭제" onCancel={() => setDeletingAudio(null)} onConfirm={() => handleDelete(deletingAudio.id)} />}
      {isUpdateModalOpen && selectedAudio && (
        <AudioUpdateModal audio={selectedAudio}
          onClose={() => { setIsUpdateModalOpen(false); setSelectedAudio(null); }}
          onSuccess={() => fetchAudioList(currentPage, appliedName)}
        />
      )}
      {isUploadModalOpen && uploadFile && (
        <AudioUploadModal file={uploadFile}
          onClose={() => { setIsUploadModalOpen(false); setUploadFile(null); }}
          onSuccess={() => fetchAudioList(currentPage, appliedName)}
        />
      )}
      {isPlayerModalOpen && playbackInfo && (
        <AudioPlayerModal url={playbackInfo.url} name={playbackInfo.name} onClose={handleClosePlayer} />
      )}
    </T.PageContainer>
  );
};

export default AudioList;
